import React, { useEffect, useMemo, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Bar, BarChart, Cell, Label, Legend, Pie, PieChart, Tooltip, XAxis, YAxis } from "recharts";

import DstButton from "../../components/DstButton/DstButton";
import MultiSelectLiveDropdown from "../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import { useSession } from "../../contexts/SessionContext";
import translate from "../../services/Translate";
import languagesArray from "../../services/languages";
import { NpsFilters, NpsItem, SurveyTypeItem } from "./InsightsNps.d";
import {
  SEGMENT_COLORS,
  chartDataFromNps,
  fetchSurveyNps,
  fetchSurveyType,
  formatNps,
  getNpsBadgeVariant,
  getPercentage,
} from "./InsightsNps.function";

const InsightsNps = () => {
  const { language } = useSession();

  const [languageSelection, setLanguageSelection] = useState<string | undefined>();
  const [surveyTypeSelection, setSurveyTypeSelection] = useState<string | undefined>();
  const [dateFrom, setDateFrom] = useState<string | undefined>();
  const [dateTo, setDateTo] = useState<string | undefined>();

  const [npsResults, setNpsResults] = useState<NpsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string>("");

  const [surveysType, setSurveysType] = useState<SurveyTypeItem[]>([]);
  const [surveysTypeLoading, setSurveysTypeLoading] = useState<boolean>(true);
  const [selectedCohortId, setSelectedCohortId] = useState<string | null>();

  const [chartType, setChartType] = useState<"pie" | "bar">("pie");

  useEffect(() => {
    fetchSurveyType(setSurveysType, setSurveysTypeLoading, language);
  }, [language]);

  const filters: NpsFilters = useMemo(
    () => ({
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      survey_type: surveyTypeSelection || undefined,
      language: languageSelection || undefined,
      cohort_id: selectedCohortId || undefined,
    }),
    [dateFrom, dateTo, surveyTypeSelection, languageSelection, selectedCohortId]
  );

  useEffect(() => {
    fetchSurveyNps(setNpsResults, setLoading, filters, language);
  }, [filters, language]);

  useEffect(() => {
    setSelectedQuestionId((prev) => {
      if (!npsResults.length) return "";
      if (!prev) return npsResults[0].question_id;
      return prev;
    });
  }, [npsResults]);

  const selectedQuestion = npsResults.find((question) => question.question_id === selectedQuestionId) || null;

  const legendPayload = useMemo(
    () =>
      selectedQuestion
        ? chartDataFromNps(selectedQuestion, language, SEGMENT_COLORS).map((segment) => ({
            value: segment.key,
            type: "square" as const,
            color: segment.color,
            id: segment.key,
          }))
        : [],
    [selectedQuestion, language]
  );

  return (
    <div className="p-md-4 p-3">
      <div className="mb-3 d-lg-flex align-items-center justify-content-between">
        <h1 className="mb-lg-0">{translate(language, "PAGES.INSIGHTS.NPS.TITLE")}</h1>
        <div className="mb-3 s-regular d-flex flex-wrap gap-2">
          <Form.Group controlId="cohortSelect">
            <Form.Label>{translate(language, "PAGES.INSIGHTS.NPS.FILTER.COHORT.TITLE")}</Form.Label>
            <MultiSelectLiveDropdown
              category={["cohorts"]}
              label={
                selectedCohortId
                  ? `${translate(language, "PAGES.INSIGHTS.NPS.FILTER.COHORT.COHORT_SELECTED")} ${selectedCohortId}`
                  : translate(language, "PAGES.INSIGHTS.NPS.FILTER.ALL")
              }
              language={language}
              mockClass="rounded-0 rounded-end"
              toggleClass="s-regular border"
              onConfirm={(selectedCohort: any) => setSelectedCohortId(selectedCohort[0]?.value.id || "")}
              isSingleSelect
            />
          </Form.Group>

          <Form.Group controlId="languageSelect">
            <Form.Label>{translate(language, "GLOBAL.LANGUAGE")}</Form.Label>
            <Form.Control
              as="select"
              className="w-100 s-regular"
              value={languageSelection ?? ""}
              onChange={(event) => {
                setLanguageSelection(event.target.value || "");
                setSelectedQuestionId("");
              }}
            >
              <option value="">{translate(language, "PAGES.INSIGHTS.NPS.FILTER.ALL")}</option>
              {Object.entries(languagesArray).map(([key, lang]: any) => (
                <option key={key} value={key}>
                  {lang.LANGUAGE}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
          <Form.Group controlId="surveyTypeSelect">
            <Form.Label>{translate(language, "PAGES.INSIGHTS.NPS.FILTER.TYPE")}</Form.Label>
            {surveysTypeLoading ? (
              <Form.Control as="select" disabled className="s-regular" style={{ width: "170px" }}>
                <option value="">
                  <span className="placeholder-glow">
                    <span className="placeholder" />
                  </span>
                </option>
              </Form.Control>
            ) : (
              <Form.Control
                as="select"
                value={surveyTypeSelection}
                onChange={(event) => {
                  setSurveyTypeSelection(event.target.value);
                  setSelectedQuestionId("");
                }}
                className="s-regular"
              >
                <option value="">{translate(language, "PAGES.INSIGHTS.NPS.FILTER.ALL")}</option>
                {Array.isArray(surveysType) &&
                  surveysType.map((surveyType, index) => (
                    <option key={surveyType.value || index} value={surveyType.value}>
                      {surveyType.survey_name}
                    </option>
                  ))}
              </Form.Control>
            )}
          </Form.Group>
          <Form.Group controlId="dateFrom">
            <Form.Label>{translate(language, "PAGES.INSIGHTS.NPS.FILTER.DATE_FROM")}</Form.Label>
            <Form.Control
              type="date"
              value={dateFrom || ""}
              onChange={(event) => setDateFrom(event.target.value || undefined)}
              className="s-regular"
            />
          </Form.Group>
          <Form.Group controlId="dateTo">
            <Form.Label>{translate(language, "PAGES.INSIGHTS.NPS.FILTER.DATE_TO")}</Form.Label>
            <Form.Control
              type="date"
              value={dateTo || ""}
              onChange={(event) => setDateTo(event.target.value || undefined)}
              className="s-regular"
            />
          </Form.Group>
        </div>
      </div>

      {loading ? (
        <div className="d-flex align-items-center gap-2">
          <div className="spinner-border" role="status" />
          <span>{translate(language, "PAGES.INSIGHTS.NPS.LOADING")}</span>
        </div>
      ) : !npsResults.length ? (
        <p className="text-muted">{translate(language, "PAGES.INSIGHTS.NPS.NO_DATA")}</p>
      ) : (
        <React.Fragment>
          <div className="mb-4">
            <Form.Label>{translate(language, "PAGES.INSIGHTS.NPS.CHOOSE_QUESTION")}</Form.Label>
            <Form.Control
              as="select"
              value={selectedQuestionId}
              onChange={(event) => setSelectedQuestionId(event.target.value)}
            >
              {npsResults.map((item) => (
                <option key={item.question_id} value={item.question_id}>
                  {item.text}
                </option>
              ))}
            </Form.Control>
          </div>

          {selectedQuestion && (
            <Row className="m-regular">
              <Col lg={6} className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h2 className="h3 mb-0">{translate(language, "PAGES.INSIGHTS.NPS.GRAPH.TITLE")}</h2>
                </div>
                <div className="d-flex align-items-start" id="nps-chart">
                  {chartType === "pie" ? (
                    <PieChart width={420} height={300}>
                      <Pie
                        data={chartDataFromNps(selectedQuestion, language, SEGMENT_COLORS)}
                        dataKey="count"
                        nameKey="key"
                        cx={200}
                        cy={140}
                        innerRadius={50}
                        outerRadius={100}
                        isAnimationActive={false}
                      >
                        {chartDataFromNps(selectedQuestion, language, SEGMENT_COLORS).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}

                        <Label
                          position="center"
                          content={({ viewBox }) => {
                            const { cx, cy } = viewBox as { cx: number; cy: number };

                            if (typeof cx !== "number" || typeof cy !== "number") return null;

                            const variant = getNpsBadgeVariant(selectedQuestion.nps);
                            const color =
                              variant === "success"
                                ? SEGMENT_COLORS.promoters
                                : variant === "warning"
                                ? SEGMENT_COLORS.passives
                                : SEGMENT_COLORS.detractors;
                            return (
                              <React.Fragment>
                                <text
                                  x={cx}
                                  y={cy - 6}
                                  textAnchor="middle"
                                  dominantBaseline="central"
                                  className="m-bold"
                                  fill="var(--color-dark-gray)"
                                >
                                  {translate(language, "PAGES.INSIGHTS.NPS.SCORE")}
                                </text>
                                <text
                                  x={cx}
                                  y={cy + 12}
                                  textAnchor="middle"
                                  dominantBaseline="central"
                                  className="h3"
                                  fill={color}
                                >
                                  {formatNps(selectedQuestion.nps)}
                                </text>
                              </React.Fragment>
                            );
                          }}
                        />
                      </Pie>

                      <Tooltip
                        formatter={(value: any, _name: any, { payload }: any) => [
                          `${value} (${payload.percent}%)`,
                          translate(language, "PAGES.INSIGHTS.NPS.GRAPH.TOOLTIP.RESPONSES"),
                        ]}
                      />

                      <Legend payload={legendPayload} />
                    </PieChart>
                  ) : (
                    <BarChart
                      width={420}
                      height={300}
                      data={chartDataFromNps(selectedQuestion, language, SEGMENT_COLORS)}
                    >
                      <XAxis dataKey="key" hide={true} />
                      <YAxis allowDecimals={false} />
                      <Tooltip
                        formatter={(value: any, _name: any, { payload }: any) => [
                          `${value} (${payload.percent}%)`,
                          translate(language, "PAGES.INSIGHTS.NPS.GRAPH.TOOLTIP.RESPONSES"),
                        ]}
                      />

                      <Legend payload={legendPayload} />
                      <Bar dataKey="count" name="Responses">
                        {chartDataFromNps(selectedQuestion, language, SEGMENT_COLORS).map((entry, index) => (
                          <Cell key={`bar-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  )}
                  <DstButton
                    btnClass="dst-icon-switch-horizontal-01"
                    variant="secondary"
                    clickFunction={() => setChartType(chartType === "pie" ? "bar" : "pie")}
                    value=""
                  />
                </div>
              </Col>

              <Col lg={6} className="mb-3">
                <div className="h-100">
                  <div>
                    <h2 className="h3 mb-3">{translate(language, "PAGES.INSIGHTS.NPS.TABLE.TITLE")}</h2>
                    <table className="table table-bordered mt-2">
                      <thead>
                        <tr>
                          <th>{translate(language, "PAGES.INSIGHTS.NPS.TABLE.SEGMENT")}</th>
                          <th className="text-end">{translate(language, "PAGES.INSIGHTS.NPS.TABLE.COUNT")}</th>
                          <th className="text-end">
                            {translate(language, "PAGES.INSIGHTS.NPS.TABLE.PERCENT_RESPONSES")}
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        <tr>
                          <td className="fw-bold" style={{ color: SEGMENT_COLORS.promoters }}>
                            {translate(language, "PAGES.INSIGHTS.NPS.SEGMENT.PROMOTERS")}
                          </td>
                          <td className="text-end">{selectedQuestion.promoters_list.length}</td>
                          <td className="text-end">
                            {getPercentage(selectedQuestion.promoters_list.length, selectedQuestion.total_responses)}%
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-bold" style={{ color: SEGMENT_COLORS.passives }}>
                            {translate(language, "PAGES.INSIGHTS.NPS.SEGMENT.PASSIVES")}
                          </td>
                          <td className="text-end">{selectedQuestion.passives_list.length}</td>
                          <td className="text-end">
                            {getPercentage(selectedQuestion.passives_list.length, selectedQuestion.total_responses)}%
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-bold" style={{ color: SEGMENT_COLORS.detractors }}>
                            {translate(language, "PAGES.INSIGHTS.NPS.SEGMENT.DETRACTORS")}
                          </td>
                          <td className="text-end">{selectedQuestion.detractors_list.length}</td>
                          <td className="text-end">
                            {getPercentage(selectedQuestion.detractors_list.length, selectedQuestion.total_responses)}%
                          </td>
                        </tr>
                      </tbody>

                      <tfoot>
                        <tr className="table-light fw-bold">
                          <td>{translate(language, "PAGES.INSIGHTS.NPS.TABLE.TOTAL")}</td>
                          <td className="text-end">{selectedQuestion.total_responses}</td>
                          <td
                            className={`text-end text-${
                              getNpsBadgeVariant(selectedQuestion.nps) === "success"
                                ? "success"
                                : getNpsBadgeVariant(selectedQuestion.nps) === "warning"
                                ? "warning"
                                : "danger"
                            }`}
                            title={translate(language, "PAGES.INSIGHTS.NPS.TABLE.TITLE_NPS_FORMULA")}
                          >
                            {translate(language, "PAGES.INSIGHTS.NPS.TABLE.GLOBAL_NPS")}:
                            {formatNps(selectedQuestion.nps)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </React.Fragment>
      )}
    </div>
  );
};

export default InsightsNps;
